import { useCallback, useRef, useMemo } from 'react'
import {
    ScoopText,
    ScoopAccordion,
    ScoopChip,
    ScoopColumn,
    ScoopRow,
    ScoopSkeleton,
    useToast,
} from '../../../../ui'
import { ReportHeader } from './components/ReportHeader/ReportHeader'
import { ReportBreadcrumb } from '../ReportBreadcrumb/ReportBreadcrumb'
import { ReportState } from '../ReportState/ReportState'
import { ReadinessPanel } from '../ReadinessPanel/ReadinessPanel'
import { ExecutiveSummary } from './components/ExecutiveSummary/ExecutiveSummary'
import { ScreeningCriteria } from './components/ScreeningCriteria/ScreeningCriteria'
import { EntityScorecard } from './components/EntityScorecard/EntityScorecard'
import { EntityDetail } from './components/EntityDetail/EntityDetail'
import { MarkdownSection } from './components/MarkdownSection/MarkdownSection'
import { actionMatchesEntity } from '../../utils/hierarchyUtils'
import { computeReadiness } from '../../utils/readiness'
import styles from './InvestigationReport.module.scss'

const formatConfidence = (value) => {
    if (value == null || value === '') return null
    const num = Number(value)
    if (isNaN(num)) return null
    return Math.round(num * 100)
}

export const InvestigationReport = ({
    report,
    loadingReport,
    reportError,
    selectedRunPath,
    generateArtifactDocx,
    isGeneratingArtifactDocx,
    actionCandidates = [],
    onAcceptAction,
    onRejectAction,
    acceptingAction,
    rejectingAction,
    pendingFingerprint,
    onViewEntity,
    variant = 'pane',
    breadcrumbs,
    terminology,
}) => {
    const toast = useToast()
    const entityRefs = useRef({})

    const handleGenerateDocx = useCallback(() => {
        if (!selectedRunPath) return
        generateArtifactDocx({
            runPath: selectedRunPath,
            onSuccess: (response) => {
                const downloadUrl = response?.url
                if (!downloadUrl) {
                    toast.error('DOCX generation returned no download link')
                    return
                }
                window.open(downloadUrl, '_blank', 'noreferrer')
                toast.success('DOCX report generated')
            },
            onError: (error) => {
                toast.error(error?.message || 'Failed to generate DOCX report')
            },
        })
    }, [generateArtifactDocx, selectedRunPath, toast])

    const entitySummaries = report?.entitySummaries || []
    const isGroupedReport =
        report?.artifactType === 'investigation_group' ||
        selectedRunPath?.includes('/investigation-groups/')

    // Publish-readiness checklist (design §5.3a): the trust signals that used
    // to be scattered across quiet text lines, composed into one surface.
    const readiness = useMemo(
        () => (report ? computeReadiness(report, entitySummaries) : null),
        [report, entitySummaries]
    )

    const handleEntityClick = useCallback(
        (entityId) => {
            if (onViewEntity) {
                const entity = entitySummaries.find((item) => item.entityId === entityId)
                if (entity) {
                    onViewEntity({
                        ...entity,
                        runPath: entity.runPath || selectedRunPath,
                    })
                    return
                }
            }

            const el = entityRefs.current[entityId]
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        },
        [entitySummaries, onViewEntity, selectedRunPath]
    )

    // Match probes to entities
    const probesByEntity = useMemo(() => {
        const probeIndex = report?.probeIndex || []
        const map = {}
        probeIndex.forEach((probe) => {
            const key = probe.entityId || '_unassigned'
            if (!map[key]) map[key] = []
            map[key].push(probe)
        })
        return map
    }, [report?.probeIndex])

    const actionsByEntity = useMemo(() => {
        const map = {}
        entitySummaries.forEach((entity) => {
            map[entity.entityId] = actionCandidates.filter((candidate) =>
                actionMatchesEntity(candidate, entity)
            )
        })
        return map
    }, [actionCandidates, entitySummaries])

    if (!selectedRunPath) {
        return (
            <ReportState
                title="Select an investigation run"
                message="Choose a run from the left to view the investigation report."
            />
        )
    }

    // Skeleton shaped like the destination (design §4): masthead, stat strip,
    // table — never a lone spinner for a full document.
    if (loadingReport) {
        return (
            <div className={`${styles.reportContainer} ${styles.reportPane}`}>
                <div className={styles.reportContent}>
                    <ScoopSkeleton variant="text" width="40%" height={28} />
                    <ScoopSkeleton variant="text" width="60%" height={16} />
                    <ScoopSkeleton variant="rounded" height={80} />
                    <ScoopSkeleton variant="rounded" height={96} />
                    <ScoopSkeleton variant="rounded" height={260} />
                </div>
            </div>
        )
    }

    if (reportError) {
        return (
            <ReportState
                variant="error"
                title="Error loading report"
                message={reportError.message}
            />
        )
    }

    const summary = report?.summary
    const request = report?.request
    // Customer-language noun for section titles (design D24 — hardcoded
    // business nouns are a bug; terminology flows from metadata.terminology).
    const entitiesNoun = terminology?.label?.('entities', 'Entities') || 'Entities'
    const investigations = report?.investigations || []
    const contextMetadata = report?.contextMetadata
    const hasContextMetadata = contextMetadata && Object.keys(contextMetadata).length > 0

    // Derive title from contextMetadata or default
    const reportTitle =
        hasContextMetadata && contextMetadata.displayName
            ? contextMetadata.displayName
            : 'Investigation Report'

    const variantClass = variant === 'page' ? styles.reportPage : styles.reportPane

    return (
        <div className={`${styles.reportContainer} ${variantClass}`}>
            <div className={styles.reportContent}>
                {breadcrumbs?.length > 1 && <ReportBreadcrumb crumbs={breadcrumbs} />}
                <ReportHeader
                    title={reportTitle}
                    entityType={request?.entityType}
                    status={report?.status}
                    timestamp={report?.timestamp}
                />

                <ExecutiveSummary
                    entitiesNoun={entitiesNoun}
                    bluf={summary?.bluf}
                    severityAssessment={summary?.severityAssessment}
                    narrative={summary?.narrative}
                    totalInvestigations={summary?.totalInvestigations}
                    highConfidenceCount={summary?.highConfidenceCount}
                    entitySummaries={entitySummaries}
                />

                {hasContextMetadata && <ScreeningCriteria contextMetadata={contextMetadata} />}

                {/* The publish decision gets one home (design §5.3a): readiness
                    checklist + the publish actions. Portal preview stays
                    group-runs-only (the endpoint rejects other run types). */}
                {readiness && (
                    <ReadinessPanel
                        readiness={readiness}
                        runPath={selectedRunPath}
                        showPortalPreview={isGroupedReport}
                        entitiesNoun={entitiesNoun}
                        onGenerateDocx={selectedRunPath ? handleGenerateDocx : undefined}
                        isGeneratingDocx={isGeneratingArtifactDocx}
                    />
                )}

                {entitySummaries.length > 0 && (
                    <EntityScorecard
                        entitySummaries={entitySummaries}
                        entitiesNoun={entitiesNoun}
                        entityTypeLabel={request?.entityType || 'Entity'}
                        onEntityClick={handleEntityClick}
                    />
                )}

                {entitySummaries.length > 0 && !isGroupedReport && (
                    <div className={styles.entityDetailsSection}>
                        <ScoopText className={styles.sectionTitle}>
                            {entitySummaries[0]?.entityType || request?.entityType || 'Entity'}{' '}
                            Details ({entitySummaries.length})
                        </ScoopText>
                        {entitySummaries.map((entity) => (
                            <EntityDetail
                                key={entity.entityId}
                                ref={(el) => {
                                    entityRefs.current[entity.entityId] = el
                                }}
                                entity={entity}
                                probes={probesByEntity[entity.entityId] || []}
                                actionCandidates={actionsByEntity[entity.entityId] || []}
                                onAcceptAction={onAcceptAction}
                                onRejectAction={onRejectAction}
                                acceptingAction={acceptingAction}
                                rejectingAction={rejectingAction}
                                pendingFingerprint={pendingFingerprint}
                                runPath={selectedRunPath}
                            />
                        ))}
                    </div>
                )}

                {/* Legacy sub-investigations (shown when no entitySummaries) */}
                {entitySummaries.length === 0 && investigations.length > 0 && (
                    <div className={styles.investigationsSection}>
                        <ScoopText className={styles.sectionTitle}>
                            Sub-Investigations ({investigations.length})
                        </ScoopText>
                        {investigations.map((inv, idx) => {
                            const invConfidence = formatConfidence(inv.confidence)
                            return (
                                <ScoopAccordion key={inv.id || idx}>
                                    <ScoopAccordion.Summary>
                                        <ScoopRow className={styles.invSummary}>
                                            <ScoopText className={styles.invName}>
                                                {inv.contextName || `Investigation ${idx + 1}`}
                                            </ScoopText>
                                            {invConfidence != null && (
                                                <ScoopChip
                                                    size="small"
                                                    label={`${invConfidence}%`}
                                                />
                                            )}
                                            {inv.status && (
                                                <ScoopChip
                                                    size="small"
                                                    color={
                                                        inv.status === 'SUCCESS'
                                                            ? 'success'
                                                            : 'default'
                                                    }
                                                    label={inv.status}
                                                />
                                            )}
                                        </ScoopRow>
                                    </ScoopAccordion.Summary>
                                    <ScoopAccordion.Details>
                                        <ScoopColumn className={styles.invDetails}>
                                            {inv.spawnReasoning && (
                                                <div className={styles.invField}>
                                                    <ScoopText className={styles.invFieldLabel}>
                                                        Reasoning
                                                    </ScoopText>
                                                    <ScoopText className={styles.invFieldValue}>
                                                        {inv.spawnReasoning}
                                                    </ScoopText>
                                                </div>
                                            )}
                                            {inv.findings && inv.findings.length > 0 && (
                                                <div className={styles.invField}>
                                                    <ScoopText className={styles.invFieldLabel}>
                                                        Findings
                                                    </ScoopText>
                                                    <ul className={styles.findingsList}>
                                                        {inv.findings.map((finding, fIdx) => (
                                                            <li
                                                                key={fIdx}
                                                                className={styles.findingItem}
                                                            >
                                                                <MarkdownSection
                                                                    content={finding.message}
                                                                />
                                                                {finding.confidence != null && (
                                                                    <ScoopText
                                                                        className={
                                                                            styles.findingConfidence
                                                                        }
                                                                    >
                                                                        Confidence:{' '}
                                                                        {formatConfidence(
                                                                            finding.confidence
                                                                        )}
                                                                        %
                                                                    </ScoopText>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </ScoopColumn>
                                    </ScoopAccordion.Details>
                                </ScoopAccordion>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
